import React, { useState, useEffect, useCallback } from 'react';
import { X, CheckCircle } from 'lucide-react';
import CountryCodePicker, { getPhoneDigits } from './CountryCodePicker';
import { TurnstileWidget } from './TurnstileWidget';
import './PlanInterestModal.css';

const PLAN_META = {
    Trial:      { className: 'trial',      label: 'Trial Plan' },
    Standard:   { className: 'standard',   label: 'Standard Plan' },
    Premium:    { className: 'premium',    label: 'Premium Plan' },
    Enterprise: { className: 'enterprise', label: 'Enterprise Plan' },
};

export default function PlanInterestModal({ plan, onClose, onSubmit }) {
    const [step, setStep] = useState(1); // 1 = form, 2 = confirm, 3 = success
    const [countryCode, setCountryCode] = useState('+91');
    const [form, setForm] = useState({
        institutionName: '',
        contactPerson: '',
        email: '',
        phone: '',
        message: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [turnstileToken, setTurnstileToken] = useState(null);

    const meta = PLAN_META[plan] || PLAN_META.Trial;
    const requiredDigits = getPhoneDigits(countryCode);

    // Reset phone when country changes so stale digits don't carry over
    const handleCountryChange = (code) => {
        setCountryCode(code);
        setForm((prev) => ({ ...prev, phone: '' }));
    };

    // Close on Escape
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') onClose();
    }, [onClose]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [handleKeyDown]);

    const set = (field) => (e) =>
        setForm((prev) => ({ ...prev, [field]: e.target.value }));

    const handlePhoneChange = (e) =>
        setForm((prev) => ({ ...prev, phone: e.target.value.replace(/\D/g, '').slice(0, requiredDigits) }));

    const handleFormSubmit = (e) => {
        e.preventDefault();
        setError('');
        if (form.phone.length !== requiredDigits) {
            setError(`Please enter a valid ${requiredDigits}-digit phone number for the selected country.`);
            return;
        }
        setStep(2);
    };

    const handleConfirmSubmit = async () => {
        if (!turnstileToken) {
            setError('Please complete the verification challenge.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            await onSubmit({ ...form, selectedPlan: plan, countryCode, turnstileToken });
            setStep(3);
        } catch (err) {
            setError(err.message || 'Something went wrong. Please try again.');
            setStep(2);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="pim-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="pim-sheet" role="dialog" aria-modal="true">
                {step !== 3 && (
                    <button className="pim-close" onClick={onClose} aria-label="Close">
                        <X size={16} />
                    </button>
                )}

                {/* ── Step 1: Form ── */}
                {step === 1 && (
                    <>
                        <div className="pim-plan-badge">
                            <span className={`pim-plan-pill ${meta.className}`}>
                                <span className="pim-plan-pill-dot" />
                                {meta.label}
                            </span>
                        </div>
                        <div className="pim-body">
                            <h2 className="pim-title">Tell us about your institution</h2>
                            <p className="pim-subtitle">No payment needed. Our team will reach out to walk you through everything.</p>

                            <form onSubmit={handleFormSubmit} noValidate>
                                <div className="pim-row">
                                    <div className="pim-field">
                                        <label htmlFor="pim-institution">Institution Name *</label>
                                        <input
                                            id="pim-institution"
                                            type="text"
                                            required
                                            placeholder="Your Institution"
                                            value={form.institutionName}
                                            onChange={set('institutionName')}
                                            autoFocus
                                        />
                                    </div>
                                    <div className="pim-field">
                                        <label htmlFor="pim-person">Contact Person *</label>
                                        <input
                                            id="pim-person"
                                            type="text"
                                            required
                                            placeholder="Your Name"
                                            value={form.contactPerson}
                                            onChange={set('contactPerson')}
                                        />
                                    </div>
                                </div>

                                <div className="pim-field">
                                    <label htmlFor="pim-email">Work Email *</label>
                                    <input
                                        id="pim-email"
                                        type="email"
                                        required
                                        placeholder="you@institution.edu.in"
                                        value={form.email}
                                        onChange={set('email')}
                                    />
                                </div>

                                <div className="pim-field">
                                    <label htmlFor="pim-phone">Phone *</label>
                                    <div className="pim-phone-row">
                                        <CountryCodePicker
                                            value={countryCode}
                                            onChange={handleCountryChange}
                                        />
                                        <input
                                            id="pim-phone"
                                            type="tel"
                                            required
                                            placeholder={'X'.repeat(requiredDigits)}
                                            maxLength={requiredDigits}
                                            value={form.phone}
                                            onChange={handlePhoneChange}
                                        />
                                    </div>
                                </div>

                                <div className="pim-field">
                                    <label htmlFor="pim-message">
                                        Anything you'd like us to know?{' '}
                                        <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(optional)</span>
                                    </label>
                                    <textarea
                                        id="pim-message"
                                        rows={3}
                                        placeholder="Tell us about your institution's scale, goals, or any questions you have."
                                        value={form.message}
                                        onChange={set('message')}
                                    />
                                </div>

                                {error && <div className="pim-error">{error}</div>}

                                <button type="submit" className="pim-btn-primary">
                                    Review and Submit
                                </button>
                            </form>
                        </div>
                    </>
                )}

                {/* ── Step 2: Confirm ── */}
                {step === 2 && (
                    <>
                        <div className="pim-plan-badge">
                            <span className={`pim-plan-pill ${meta.className}`}>
                                <span className="pim-plan-pill-dot" />
                                {meta.label}
                            </span>
                        </div>
                        <div className="pim-body">
                            <h2 className="pim-title">Confirm your request</h2>
                            <p className="pim-subtitle">Take a moment to review your details before we send this to our team.</p>

                            <div className="pim-confirm-notice">
                                <p className="pim-confirm-notice-title">Your details</p>
                                <div className="pim-confirm-row">
                                    <span className="pim-confirm-label">Institution</span>
                                    <span className="pim-confirm-value">{form.institutionName}</span>
                                </div>
                                <div className="pim-confirm-row">
                                    <span className="pim-confirm-label">Contact</span>
                                    <span className="pim-confirm-value">{form.contactPerson}</span>
                                </div>
                                <div className="pim-confirm-row">
                                    <span className="pim-confirm-label">Email</span>
                                    <span className="pim-confirm-value">{form.email}</span>
                                </div>
                                <div className="pim-confirm-row">
                                    <span className="pim-confirm-label">Phone</span>
                                    <span className="pim-confirm-value">{countryCode} {form.phone}</span>
                                </div>
                                <div className="pim-confirm-row">
                                    <span className="pim-confirm-label">Plan</span>
                                    <span className="pim-confirm-value">{plan}</span>
                                </div>
                            </div>

                            <p className="pim-consent-text">
                                By submitting, you agree that BulandiPe's marketing team may contact you to discuss pricing, onboarding, and how the platform fits your institution's needs. No payment is required at this stage.
                            </p>

                            <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken(null)} />

                            {error && <div className="pim-error">{error}</div>}

                            <div className="pim-btn-row">
                                <button
                                    className="pim-btn-primary"
                                    onClick={handleConfirmSubmit}
                                    disabled={loading || !turnstileToken}
                                >
                                    {loading ? 'Submitting…' : 'Yes, Submit Request'}
                                </button>
                                <button
                                    className="pim-btn-ghost"
                                    onClick={() => { setError(''); setStep(1); }}
                                    disabled={loading}
                                >
                                    Go Back and Edit
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {/* ── Step 3: Success ── */}
                {step === 3 && (
                    <div className="pim-success">
                        <div className="pim-success-icon">
                            <CheckCircle size={32} />
                        </div>
                        <h2 className="pim-success-title">Request received.</h2>
                        <p className="pim-success-body">
                            We have got your details for the {plan} plan. Our team will be in touch within one business day.
                        </p>
                        <button className="pim-success-close" onClick={onClose}>
                            Done
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
